/**
 * Authentication Types for AICC Admin
 *
 * Role-based access control (RBAC) types and utilities.
 */

/**
 * User roles available in the admin system
 */
export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  ORG_ADMIN = 'ORG_ADMIN',
  POWER = 'POWER',
  USER = 'USER',
}

/**
 * User interface for authentication
 */
export interface AuthUser {
  username: string;
  name: string;
  role: string;
  roles: string[];
  email?: string;
  organizationId?: number;
  organizationName?: string;
}

/**
 * Check if user has any of the specified roles
 *
 * @param user - User object with roles array
 * @param requiredRoles - Array of roles to check against
 * @returns true if user has at least one of the required roles
 *
 * @example
 * hasAnyRole(user, [UserRole.ADMIN, UserRole.SUPER_ADMIN])
 */
export function hasAnyRole(
  user: AuthUser | null | undefined,
  requiredRoles: UserRole[]
): boolean {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }

  return requiredRoles.some((role) => user.roles.includes(role));
}

/**
 * Check if user has all of the specified roles
 *
 * @param user - User object with roles array
 * @param requiredRoles - Array of roles that are all required
 * @returns true if user has all of the required roles
 */
export function hasAllRoles(
  user: AuthUser | null | undefined,
  requiredRoles: UserRole[]
): boolean {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }

  return requiredRoles.every((role) => user.roles.includes(role));
}

/**
 * Check if user is at least an admin (ADMIN or SUPER_ADMIN)
 */
export function isAdmin(user: AuthUser | null | undefined): boolean {
  return hasAnyRole(user, [UserRole.ADMIN, UserRole.SUPER_ADMIN]);
}

/**
 * Check if user is a super admin
 */
export function isSuperAdmin(user: AuthUser | null | undefined): boolean {
  return hasAnyRole(user, [UserRole.SUPER_ADMIN]);
}
